import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { reportApi } from '../../lib/api'
import Icon from '../../components/Icon'
import Badge from '../../components/Badge'
import Spinner from '../../components/Spinner'
import { formatDateTime } from '../../lib/utils'

export default function ReportDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const justSubmitted = searchParams.get('success') === 'true'

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['report', id],
    queryFn: () => reportApi.get(id).then((res) => res.data),
  })

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !report) {
    return (
      <div className="min-h-[60vh] p-4 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Icon name="search_off" className="text-text-muted text-5xl" />
          <p className="text-body-lg text-text-secondary">Report not found</p>
          <button
            onClick={() => navigate('/reports')}
            className="inline-flex items-center gap-2 bg-accent text-surface-dark font-semibold text-label-md px-6 py-3 rounded-xl hover:bg-accent-light transition"
          >
            Back to Reports
          </button>
        </div>
      </div>
    )
  }

  const photoSrc = report.photo_url || report.photo_path

  return (
    <div className="px-4 md:px-10 py-6">
      <div className="max-w-2xl mx-auto space-y-5">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-accent text-label-md hover:underline min-h-0"
        >
          <Icon name="arrow_back" className="text-xl" />
          Back
        </button>

        {justSubmitted && (
          <div className="flex items-center gap-3 bg-status-resolved/10 border border-status-resolved/40 rounded-xl p-4">
            <Icon name="check_circle" filled className="text-status-resolved text-2xl" />
            <p className="text-body-md text-text-primary">
              Report submitted — the responsible organization has been notified.
            </p>
          </div>
        )}

        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          {photoSrc && (
            <div className="relative bg-surface-high">
              <img
                src={photoSrc}
                alt="Report"
                loading="lazy"
                className="w-full h-auto max-h-96 object-cover"
              />
              <div className="absolute top-4 right-4">
                <Badge status={report.status} />
              </div>
            </div>
          )}

          <div className="p-5 md:p-6 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <h1 className="text-headline-lg-mobile md:text-headline-md">{report.category}</h1>
                {report.location_text && (
                  <p className="flex items-center gap-1.5 text-body-md text-text-secondary">
                    <Icon name="location_on" className="text-base" />
                    {report.location_text}
                  </p>
                )}
              </div>
              {!photoSrc && <Badge status={report.status} />}
            </div>

            {report.description && (
              <div className="space-y-1.5">
                <p className="text-label-sm text-text-muted uppercase tracking-wide">Description</p>
                <p className="text-body-md text-text-primary">{report.description}</p>
              </div>
            )}

            <div className="bg-surface-low border border-border rounded-xl divide-y divide-border">
              <InfoRow icon="schedule" label="Submitted" value={formatDateTime(report.created_at)} />
              {report.acknowledged_at && (
                <InfoRow
                  icon="visibility"
                  label="Acknowledged"
                  value={formatDateTime(report.acknowledged_at)}
                />
              )}
              {report.resolved_at && (
                <InfoRow
                  icon="task_alt"
                  label="Resolved"
                  value={formatDateTime(report.resolved_at)}
                />
              )}
              {report.reference_number && (
                <InfoRow icon="tag" label="Reference" value={report.reference_number} mono />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function InfoRow({ icon, label, value, mono = false }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Icon name={icon} className="text-accent text-xl shrink-0" />
      <span className="text-label-sm text-text-muted w-28 shrink-0">{label}</span>
      <span className={`text-body-md text-text-primary ${mono ? 'font-mono text-label-md' : ''}`}>
        {value}
      </span>
    </div>
  )
}
